import axiosInstance from '../utils/axios';

/**
 * 의료진 관리 API 서비스
 */
class MedicalStaffService {
  /**
   * 의료진 목록을 조회합니다.
   * 
   * @param {Object} params 조회 파라미터
   * @param {number} params.page 페이지 번호 (기본값: 1)
   * @param {number} params.pageSize 페이지 크기 (기본값: 20)
   * @param {string} params.search 검색어
   * @param {boolean} params.isActive 활성 상태 필터
   * @param {string} params.role 역할 필터
   * @param {string} params.department 부서 필터
   * @returns {Promise<Object>} 의료진 목록 조회 결과
   */
  async getStaffList(params = {}) {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('page', params.page || 1);
      queryParams.append('pageSize', params.pageSize || 20);
      
      if (params.search) queryParams.append('search', params.search);
      if (params.isActive !== undefined) queryParams.append('isActive', params.isActive);
      if (params.role) queryParams.append('role', params.role);
      if (params.department) queryParams.append('department', params.department);
      
      const response = await axiosInstance.get(`/security/bypass/staff?${queryParams}`);

      if (response.data && response.data.success) {
        return {
          success: true,
          data: response.data.data || {}
        };
      }
      
      return {
        success: false,
        message: response.data.message || '의료진 목록을 불러오는데 실패했습니다.'
      };
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || '의료진 목록을 불러오는 중 오류가 발생했습니다.';
      return {
        success: false,
        message: errorMessage
      };
    }
  }

  /**
   * 의료진 정보를 조회합니다.
   * 
   * @param {string} staffId 의료진 ID
   * @returns {Promise<Object>} 의료진 정보 조회 결과
   */
  async getStaff(staffId) {
    try {
      const response = await axiosInstance.get(`/security/bypass/staff/${staffId}`);

      if (response.data && response.data.success) {
        return {
          success: true,
          data: response.data.data || {}
        };
      }
      
      return {
        success: false,
        message: response.data.message || '의료진 정보를 불러오는데 실패했습니다.'
      };
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || '의료진 정보를 불러오는 중 오류가 발생했습니다.';
      return {
        success: false,
        message: errorMessage
      };
    }
  }

  /**
   * 새로운 의료진을 등록합니다.
   * 
   * @param {Object} staffData 의료진 데이터
   * @returns {Promise<Object>} 의료진 등록 결과
   */
  async createStaff(staffData) {
    try {
      const response = await axiosInstance.post('/security/bypass/staff', {
        userIdx: staffData.userIdx,
        staffId: staffData.staffId,
        name: staffData.name,
        phoneNumber: staffData.phoneNumber,
        email: staffData.email || null,
        department: staffData.department || null,
        role: staffData.role || 'doctor',
        bypassLevel: staffData.bypassLevel || 1,
        maxDailyBypasses: staffData.maxDailyBypasses || 5,
        sessionDurationMinutes: staffData.sessionDurationMinutes || 30,
        allowedFeatures: staffData.allowedFeatures || ['root_bypass']
      });

      if (response.data && response.data.success) {
        return {
          success: true,
          message: response.data.message || '의료진이 성공적으로 등록되었습니다.'
        };
      }
      
      return {
        success: false,
        message: response.data.message || '의료진 등록에 실패했습니다.'
      };
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || '의료진 등록 중 오류가 발생했습니다.';
      return {
        success: false,
        message: errorMessage
      };
    }
  }

  /**
   * 의료진 정보를 수정합니다.
   * 
   * @param {string} staffId 의료진 ID
   * @param {Object} staffData 수정할 데이터
   * @returns {Promise<Object>} 의료진 수정 결과
   */
  async updateStaff(staffId, staffData) {
    try {
      const response = await axiosInstance.put(`/security/bypass/staff/${staffId}`, staffData);

      if (response.data && response.data.success) {
        return {
          success: true,
          message: response.data.message || '의료진 정보가 수정되었습니다.'
        };
      }
      
      return {
        success: false,
        message: response.data.message || '의료진 정보 수정에 실패했습니다.'
      };
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || '의료진 정보 수정 중 오류가 발생했습니다.';
      return {
        success: false,
        message: errorMessage
      };
    }
  }

  /**
   * 의료진을 삭제합니다.
   * 
   * @param {string} staffId 의료진 ID
   * @returns {Promise<Object>} 의료진 삭제 결과
   */
  async deleteStaff(staffId) {
    try {
      const response = await axiosInstance.delete(`/security/bypass/staff/${staffId}`);

      if (response.data && response.data.success) {
        return {
          success: true,
          message: response.data.message || '의료진이 삭제되었습니다.'
        };
      }
      
      return {
        success: false,
        message: response.data.message || '의료진 삭제에 실패했습니다.'
      };
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || '의료진 삭제 중 오류가 발생했습니다.';
      return {
        success: false,
        message: errorMessage
      };
    }
  }

  /**
   * 의료진 활성/비활성 상태를 변경합니다.
   * 
   * @param {string} staffId 의료진 ID
   * @param {boolean} isActive 활성 상태
   * @returns {Promise<Object>} 상태 변경 결과
   */
  async toggleStaffStatus(staffId, isActive) {
    try {
      const response = await axiosInstance.put(`/security/bypass/staff/${staffId}`, {
        isActive: isActive
      });

      if (response.data && response.data.success) {
        return {
          success: true,
          message: isActive ? '의료진이 활성화되었습니다.' : '의료진이 비활성화되었습니다.'
        };
      }
      
      return {
        success: false,
        message: response.data.message || '상태 변경에 실패했습니다.'
      };
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || '상태 변경 중 오류가 발생했습니다.';
      return {
        success: false,
        message: errorMessage
      };
    }
  }

  /**
   * 사용자 목록을 검색합니다 (의료진 연결용)
   * 
   * @param {string} search 검색어
   * @returns {Promise<Object>} 사용자 목록
   */
  async searchUsers(search) {
    try {
      // 백엔드는 keyword 파라미터를 사용하고, searchKind=1은 이름 검색, searchKind=2는 ID 검색
      const response = await axiosInstance.get(`/user/list?keyword=${encodeURIComponent(search)}&searchKind=0&pageSize=20`);

      if (response.data && response.data.success) {
        return {
          success: true,
          data: response.data.data?.users || []
        };
      }
      
      return {
        success: false,
        message: response.data.message || '사용자 검색에 실패했습니다.'
      };
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || '사용자 검색 중 오류가 발생했습니다.';
      return {
        success: false,
        message: errorMessage
      };
    }
  }
}

const medicalStaffService = new MedicalStaffService();
export default medicalStaffService;
